import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Sparkles, ChevronRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Citations d'Houphouët-Boigny
const HOUPHOUET_QUOTES = [
  {
    text: "Le dialogue est l'arme des forts.",
    author: "Félix Houphouët-Boigny"
  },
  {
    text: "La paix n'est pas un vain mot, c'est un comportement.",
    author: "Félix Houphouët-Boigny"
  },
  {
    text: "Nous préférons la liberté dans la pauvreté à la richesse dans l'esclavage.",
    author: "Félix Houphouët-Boigny"
  },
  {
    text: "Le développement ne se décrète pas, il se construit jour après jour.",
    author: "Félix Houphouët-Boigny"
  },
  {
    text: "L'Afrique a besoin de paix pour se développer.",
    author: "Félix Houphouët-Boigny"
  },
  {
    text: "On ne bâtit pas un pays sur la haine.",
    author: "Félix Houphouët-Boigny"
  },
  {
    text: "Le travail est une source de dignité et de liberté.",
    author: "Félix Houphouët-Boigny"
  },
  {
    text: "L'unité ne se décrète pas, elle se vit.",
    author: "Félix Houphouët-Boigny"
  }
];

// Navigation suggestions
const NAV_SUGGESTIONS = [
  { label: 'Découvrir notre manifeste', path: '/manifeste' },
  { label: 'Lire la Constitution', path: '/constitution' },
  { label: 'Rejoindre le mouvement', path: '/rejoindre' },
  { label: 'Faire un don', path: '/don' },
];

const FelixChatbot = () => {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [weeklyQuote, setWeeklyQuote] = useState(null);
  const [sessionId] = useState(() => `felix-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Get weekly quote based on week number
  useEffect(() => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const weekNumber = Math.ceil(((now - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
    const quoteIndex = weekNumber % HOUPHOUET_QUOTES.length;
    setWeeklyQuote(HOUPHOUET_QUOTES[quoteIndex]);
  }, []);

  // Initial greeting when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = language === 'fr' 
        ? `Bonjour ! Je suis Félix, votre guide inspiré par la sagesse du Président Houphouët-Boigny. 🇨🇮\n\nJe suis là pour vous aider à naviguer sur le site de l'Alliance Démocratique Africaine et répondre à vos questions sur notre mouvement.\n\nComment puis-je vous aider ?`
        : language === 'en'
        ? `Hello! I'm Felix, your guide inspired by the wisdom of President Houphouët-Boigny. 🇨🇮\n\nI'm here to help you navigate the African Democratic Alliance website and answer your questions about our movement.\n\nHow can I help you?`
        : `Olá! Sou Félix, o seu guia inspirado pela sabedoria do Presidente Houphouët-Boigny. 🇨🇮\n\nEstou aqui para ajudá-lo a navegar no site da Aliança Democrática Africana e responder às suas perguntas sobre o nosso movimento.\n\nComo posso ajudá-lo?`;
      
      setMessages([{
        role: 'assistant',
        content: greeting,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length, language]);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat/felix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage.content,
          session_id: sessionId,
          language: language
        })
      });

      if (!response.ok) throw new Error('Chat failed');

      const data = await response.json();
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: language === 'fr' 
          ? 'Désolé, je rencontre des difficultés. Veuillez réessayer.'
          : 'Sorry, I\'m having trouble. Please try again.',
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleNavSuggestion = (path) => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Chat Button - Felix Badge (Chat Bubble Shape) */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 right-6 z-50 w-16 h-14 flex items-center justify-center shadow-xl transition-all ${
          isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
        } bg-[#F7C948] text-[#5B2D8E] hover:bg-[#E5B93D]`}
        style={{
          borderRadius: '24px 24px 6px 24px',
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="felix-chat-button"
        aria-label="Ouvrir le chat avec Félix"
      >
        <span className="text-2xl font-bold">F</span>
        {/* Green online indicator */}
        <span className="absolute top-1 right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm">
          <span className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-75"></span>
        </span>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className={`fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] h-[600px] max-h-[calc(100vh-100px)] rounded-2xl shadow-2xl flex flex-col overflow-hidden ${
              isDark ? 'bg-[#1A0F2E] border border-white/10' : 'bg-white border border-gray-200'
            }`}
            data-testid="felix-chat-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#5B2D8E] to-[#7B4AAE]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F7C948] flex items-center justify-center text-[#5B2D8E] font-bold text-lg">
                  F
                </div>
                <div>
                  <h3 className="font-bold text-white">Félix</h3>
                  <p className="text-xs text-white/70">
                    {language === 'fr' ? 'Guide ADA' : language === 'en' ? 'ADA Guide' : 'Guia ADA'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Fermer"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Weekly Quote */}
            {weeklyQuote && (
              <div className={`px-4 py-3 border-b ${isDark ? 'bg-[#F7C948]/10 border-white/10' : 'bg-[#F7C948]/10 border-[#F7C948]/20'}`}>
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-[#F7C948] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className={`text-xs italic ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                      "{weeklyQuote.text}"
                    </p>
                    <p className={`text-[10px] mt-1 ${isDark ? 'text-[#F7C948]' : 'text-[#5B2D8E]'}`}>
                      — {weeklyQuote.author}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-[#5B2D8E] text-white rounded-br-sm'
                        : msg.isError
                        ? isDark ? 'bg-red-500/20 text-red-300' : 'bg-red-50 text-red-600'
                        : isDark
                        ? 'bg-white/10 text-white rounded-bl-sm'
                        : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className={`rounded-2xl px-4 py-3 ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                    <div className="flex items-center gap-2">
                      <Loader2 className={`w-4 h-4 animate-spin ${isDark ? 'text-[#F7C948]' : 'text-[#5B2D8E]'}`} />
                      <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                        {language === 'fr' ? 'Félix réfléchit...' : 'Felix is thinking...'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Navigation */}
            <div className={`px-4 py-2 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <p className={`text-[10px] uppercase tracking-wider mb-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                {language === 'fr' ? 'Navigation rapide' : 'Quick navigation'}
              </p>
              <div className="flex flex-wrap gap-1">
                {NAV_SUGGESTIONS.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    onClick={() => handleNavSuggestion(item.path)}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-colors ${
                      isDark 
                        ? 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                    }`}
                  >
                    {item.label}
                    <ChevronRight className="w-3 h-3" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Input */}
            <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <div className={`flex items-center gap-2 rounded-xl px-4 py-2 ${
                isDark ? 'bg-white/5' : 'bg-gray-100'
              }`}>
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={language === 'fr' ? 'Posez votre question...' : 'Ask your question...'}
                  className={`flex-1 bg-transparent outline-none text-sm ${
                    isDark ? 'text-white placeholder:text-white/40' : 'text-gray-800 placeholder:text-gray-400'
                  }`}
                  disabled={isLoading}
                  data-testid="felix-chat-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    inputValue.trim() && !isLoading
                      ? 'bg-[#5B2D8E] text-white hover:bg-[#4A2472]'
                      : isDark ? 'bg-white/10 text-white/30' : 'bg-gray-200 text-gray-400'
                  }`}
                  data-testid="felix-chat-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FelixChatbot;
